import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { FirebaseApp, initializeApp } from 'firebase/app';
import {
  Auth,
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  initializeAuth,
} from 'firebase/auth';
import { connectFirestoreEmulator, Firestore, getFirestore } from 'firebase/firestore';
import {
  connectStorageEmulator,
  FirebaseStorage,
  getStorage,
} from 'firebase/storage';

import {
  firebaseEmulatorHosts,
  firebaseOptions,
  useFirebaseEmulators,
} from './firebase.config';
import {
  FIREBASE_APP,
  FIREBASE_AUTH,
  FIREBASE_FIRESTORE,
  FIREBASE_STORAGE,
} from './firebase.tokens';

interface FirebaseBundle {
  app: FirebaseApp;
  auth: Auth;
  firestore: Firestore;
  storage: FirebaseStorage;
}

let firebaseBundle: FirebaseBundle | null = null;

function createAuth(app: FirebaseApp): Auth {
  try {
    return initializeAuth(app, { persistence: browserLocalPersistence });
  } catch {
    return getAuth(app);
  }
}

function getFirebaseBundle(): FirebaseBundle {
  if (firebaseBundle) {
    return firebaseBundle;
  }

  const app = initializeApp(firebaseOptions);
  const auth = createAuth(app);

  if (useFirebaseEmulators) {
    connectAuthEmulator(auth, firebaseEmulatorHosts.auth, { disableWarnings: true });
  }

  const firestore = getFirestore(app);
  const storage = getStorage(app);

  if (useFirebaseEmulators) {
    const [firestoreHost = '127.0.0.1', firestorePort = '8080'] =
      firebaseEmulatorHosts.firestore.split(':');
    connectFirestoreEmulator(firestore, firestoreHost, Number(firestorePort));
    const [storageHost = '127.0.0.1', storagePort = '9199'] =
      firebaseEmulatorHosts.storage.split(':');
    connectStorageEmulator(storage, storageHost, Number(storagePort));
  }

  firebaseBundle = { app, auth, firestore, storage };
  return firebaseBundle;
}

export function provideFirebase(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: FIREBASE_APP,
      useFactory: (): FirebaseApp => getFirebaseBundle().app,
    },
    {
      provide: FIREBASE_AUTH,
      useFactory: (): Auth => getFirebaseBundle().auth,
    },
    {
      provide: FIREBASE_FIRESTORE,
      useFactory: (): Firestore => getFirebaseBundle().firestore,
    },
    {
      provide: FIREBASE_STORAGE,
      useFactory: (): FirebaseStorage => getFirebaseBundle().storage,
    },
  ]);
}
